import React from 'react';
import { Button, Tooltip } from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import { formatDateAndTimeFromNowWithTooltip } from '@src/helpers/utils';
import { EvaluationResult } from './types';
import { describeRun, shortRunId } from './utils';

/**
 * The score and the button that refreshes it, on the card's header — the two
 * things worth knowing about the suite without opening it.
 *
 * Nothing about individual cases lives here: that is all one row of the table
 * inside, so a case and its verdict are never said twice.
 */
const EvaluationRunHeader = ({
    result,
    /** why the run cannot be started, when it cannot */
    blocked,
    starting,
    notice,
    onRun,
}: {
    result: EvaluationResult;
    blocked: string | null;
    starting: boolean;
    notice: string | null;
    onRun: () => void;
}) => {
    const status = describeRun(result);

    const button = (
        <Button
            className="aip-btn"
            type="primary"
            htmlType="button"
            icon={<PlayCircleOutlined />}
            loading={starting}
            disabled={!!blocked}
            onClick={onRun}
        >
            Run evaluation
        </Button>
    );

    return (
        <div className="aip-run">
            {/* the score and what it is a score of, on one line — the number
                alone says too little and a second line says it too loudly */}
            <p className="aip-run__score">
                <span className={`aip-run__value aip-run__value--${status.tone}`}>{status.label}</span>
                <span className="aip-run__detail">
                    {status.detail}
                    {result.run_id && (
                        <>
                            {status.detail ? ' · ' : ''}
                            {result.started && <>{formatDateAndTimeFromNowWithTooltip(result.started)} · </>}
                            <span className="aip-run__id">run {shortRunId(result.run_id)}</span>
                        </>
                    )}
                </span>
            </p>

            <div className="aip-run__act">
                {/* a disabled button cannot carry a tooltip of its own, so the
                    reason is wrapped around it rather than put on it */}
                {blocked ? (
                    <Tooltip title={blocked}>
                        <span>{button}</span>
                    </Tooltip>
                ) : (
                    button
                )}
            </div>

            {notice && (
                <p className="aip-run__notice" role="alert">
                    {notice}
                </p>
            )}
        </div>
    );
};

export default EvaluationRunHeader;
